﻿/**
 * Session 的儲存。
 *
 * 刻意做成可抽換的介面：預設用 localStorage（零後端），
 * 但同一個介面也可以由 bridge app 接到 QSyn，讓工作區跨裝置。
 * concept.md §7.1「Desktop 是短暫的，持久性是後端的責任」因此仍然成立——
 * 這裡存的只是「哪些視窗在哪裡」，不是 app 的資料。
 */

import type { SessionSnapshot } from '@qdesktop/kernel'
import { isAvailable, openKeyValueStore } from './idb.ts'

export interface SessionStore {
  load(): Promise<SessionSnapshot | null>
  save(snapshot: SessionSnapshot): Promise<void>
  clear(): Promise<void>
}

const DEFAULT_KEY = 'qdesktop.session'

/**
 * 預設的存檔位置：IndexedDB，開不起來就退回 localStorage。
 *
 * IndexedDB 的配額到幾百 MB 且非同步；localStorage 只有 5MB 而且同步，
 * 每次存檔都會卡住主執行緒。無痕模式下 IndexedDB 可能不可用，
 * 那時退回 localStorage 至少還能記住視窗位置——**存不了檔不該讓桌面開不起來**。
 */
export function defaultSession(key = DEFAULT_KEY): SessionStore {
  return isAvailable() ? indexedDbSession(key) : localStorageSession(key)
}

export function indexedDbSession(key = DEFAULT_KEY, dbName = 'qdesktop'): SessionStore {
  const store = openKeyValueStore(dbName)
  return {
    async load() {
      try {
        const value = await store.get<unknown>(key)
        return isValidSnapshot(value) ? value : null
      } catch {
        return null
      }
    },
    async save(snapshot) {
      try {
        await store.set(key, snapshot)
      } catch {
        /* 配額用盡或被停用時就不存，不要影響桌面運作 */
      }
    },
    async clear() {
      try {
        await store.delete(key)
      } catch {
        /* 忽略 */
      }
      // 舊版的存檔在 localStorage，一起清掉才不會被還原回來
      try {
        localStorage.removeItem(key)
      } catch {
        /* 忽略 */
      }
    },
  }
}

export function localStorageSession(key = DEFAULT_KEY): SessionStore {
  return {
    async load() {
      let raw: string | null = null
      try {
        raw = localStorage.getItem(key)
      } catch {
        return null // 無痕模式或被停用
      }
      if (!raw) return null

      try {
        const parsed = JSON.parse(raw) as unknown
        return isValidSnapshot(parsed) ? parsed : null
      } catch {
        // 壞掉的存檔不該讓桌面開不起來
        try {
          localStorage.removeItem(key)
        } catch {
          /* 忽略 */
        }
        return null
      }
    },

    async save(snapshot) {
      try {
        localStorage.setItem(key, JSON.stringify(snapshot))
      } catch {
        /* 配額用盡或被停用時就不存，不要影響桌面運作 */
      }
    },

    async clear() {
      try {
        localStorage.removeItem(key)
      } catch {
        /* 忽略 */
      }
    },
  }
}

/** 舊版或損毀的存檔一律視為沒有存檔，寧可空桌面也不要半殘的桌面 */
function isValidSnapshot(value: unknown): value is SessionSnapshot {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<SessionSnapshot>
  if (candidate.version !== 1) return false
  if (!Array.isArray(candidate.instances)) return false
  return candidate.instances.every(
    (instance) =>
      typeof instance?.uid === 'string' &&
      typeof instance?.url === 'string' &&
      typeof instance?.rect?.x === 'number' &&
      typeof instance?.rect?.y === 'number',
  )
}
